import type { LandingTheme } from './theme';

/**
 * The public landing page's content, as the SiteContent context stores it.
 *
 * One document per practitioner, edited as a whole and saved against a
 * `version` (optimistic concurrency): two admins editing at once must never
 * silently overwrite each other.
 *
 * Every string is plain text. Image fields hold the id of an uploaded
 * `LandingImage` or null — the panel resolves the id to a preview through the
 * image list the editor endpoint returns alongside the document.
 */

export const LANDING_TERM_ICONS = ['wallet', 'clock', 'user', 'list', 'shield', 'doc'] as const;
export type LandingTermIcon = (typeof LANDING_TERM_ICONS)[number];

/** «شکل قاب عکس» — the hero photo's frame. */
export const LANDING_PHOTO_FRAMES = ['rounded', 'arch'] as const;
export type LandingPhotoFrame = (typeof LANDING_PHOTO_FRAMES)[number];

/** «اندازهٔ لوگو» — three fixed steps the header already fits at every width. */
export const LANDING_LOGO_SIZES = ['small', 'medium', 'large'] as const;
export type LandingLogoSize = (typeof LANDING_LOGO_SIZES)[number];

/**
 * «نمایش در صفحه» — the sections that can be hidden. Hiding keeps the content;
 * the hero is the page and is never hidden, and the notice, ad and gate have
 * their own switches.
 */
export const LANDING_SECTION_KEYS = [
  'heroFacts',
  'aboutDoctor',
  'aboutUs',
  'bookingTerms',
  'bookingGuide',
  'faq',
  'contact',
] as const;
export type LandingSectionKey = (typeof LANDING_SECTION_KEYS)[number];
export type LandingSections = Readonly<Record<LandingSectionKey, boolean>>;

export const LANDING_CHANNEL_PLATFORMS = ['bale', 'telegram', 'instagram'] as const;
export type LandingChannelPlatform = (typeof LANDING_CHANNEL_PLATFORMS)[number];

export interface LandingNotice {
  readonly enabled: boolean;
  readonly barText: string;
  readonly title: string;
  /** HTML from the rich-text editor, rebuilt by the server from an allowlist. */
  readonly body: string;
  readonly dateLabel: string;
  /** Repeat the notice on the booking app, not only on the landing. */
  readonly showOnBooking: boolean;
}

/**
 * «پیام پیش از نوبت‌گیری» — the window the landing page opens when a patient
 * presses a booking button, before handing them to the booking app. Off, or
 * with an empty body, the buttons go straight there as they always did.
 */
export interface LandingBookingGate {
  readonly enabled: boolean;
  readonly title: string;
  readonly body: string;
  /** The label of the button that carries on to the booking app. */
  readonly buttonLabel: string;
}

export interface LandingHero {
  readonly eyebrow: string;
  readonly doctorName: string;
  readonly specialty: string;
  readonly city: string;
  readonly intro: string;
  readonly doctorPhotoId: string | null;
  readonly badgeTitle: string;
  readonly badgeText: string;
  readonly photoFrame: LandingPhotoFrame;
  /** «مشاوره آنلاین» beside «دریافت نوبت». */
  readonly consultationCta: boolean;
}

export interface LandingAdBanner {
  readonly enabled: boolean;
  readonly imageId: string | null;
  readonly linkUrl: string | null;
  readonly alt: string;
}

export interface LandingCredential {
  readonly label: string;
  readonly value: string;
}

export interface LandingHours {
  readonly day: string;
  readonly value: string;
}

export interface LandingRoute {
  readonly label: string;
  readonly url: string;
}

export interface LandingTerm {
  readonly icon: LandingTermIcon;
  readonly title: string;
  readonly body: string;
}

export interface LandingGuideStep {
  readonly title: string;
  readonly body: string;
}

export interface LandingFaq {
  /** «گروه» — empty means the question stands on its own, under no heading. */
  readonly group: string;
  readonly question: string;
  readonly answer: string;
}

export interface LandingChannel {
  readonly platform: LandingChannelPlatform;
  readonly url: string;
}

export interface LandingContact {
  readonly adminContactUrl: string | null;
  readonly adminContactText: string;
  /** The manager's small round photo beside «ارتباط با مدیریت», or none. */
  readonly adminPhotoId: string | null;
  /** The one name used on every messenger, without the @. */
  readonly channelHandle: string;
  readonly channels: readonly LandingChannel[];
  readonly phones: readonly string[];
  readonly phoneHours: string;
}

export interface LandingDocument {
  readonly notice: LandingNotice;
  readonly bookingGate: LandingBookingGate;
  /** «پیش از پرداخت بررسی آزمایش» — opened in the booking app, same shape. */
  readonly labReviewGate: LandingBookingGate;
  /** «پیش از پرداخت مشاوره» — opened in the booking app, same shape. */
  readonly consultationGate: LandingBookingGate;
  readonly hero: LandingHero;
  readonly adBanner: LandingAdBanner;
  readonly aboutDoctor: {
    readonly heading: string;
    readonly bio: string;
    readonly credentials: readonly LandingCredential[];
    readonly services: readonly string[];
  };
  readonly aboutUs: {
    readonly heading: string;
    readonly intro: string;
    readonly hours: readonly LandingHours[];
    readonly routes: readonly LandingRoute[];
  };
  readonly bookingTerms: readonly LandingTerm[];
  readonly bookingGuide: readonly LandingGuideStep[];
  readonly faq: readonly LandingFaq[];
  readonly contact: LandingContact;
  readonly footer: {
    readonly tagline: string;
    readonly copyright: string;
    readonly legalNote: string;
    /** The doctor's small round photo in the footer, or null. */
    readonly photoId: string | null;
    /** The eNamad badge's two values; both empty means no badge. */
    readonly enamadId: string;
    readonly enamadCode: string;
  };
  readonly seo: {
    readonly title: string;
    readonly description: string;
    readonly ogImageId: string | null;
  };
  /** رنگ‌بندی — the landing's palette; the booking app is never themed. */
  readonly theme: LandingTheme;
  readonly branding: LandingBranding;
  readonly sections: LandingSections;
  /** «نسخه انگلیسی» — the page's words in English; everything else is shared. */
  readonly en: LandingEnglish;
}

/**
 * The English page's words. Every field the English page shows comes from here
 * — an empty one is empty in English, never the Persian — and each list is its
 * own. Photos, links, phones, colours and visibility are the page's, shared.
 * `/en` exists only while `enabled` is on and published.
 */
export interface LandingEnglish {
  readonly enabled: boolean;
  /** Scheduling's address is Persian; this is the one the English page shows. */
  readonly addressLine: string;
  readonly notice: Pick<LandingNotice, 'barText' | 'title' | 'body' | 'dateLabel'>;
  readonly bookingGate: Pick<LandingBookingGate, 'title' | 'body' | 'buttonLabel'>;
  readonly labReviewGate: Pick<LandingBookingGate, 'title' | 'body' | 'buttonLabel'>;
  readonly consultationGate: Pick<LandingBookingGate, 'title' | 'body' | 'buttonLabel'>;
  readonly hero: Pick<
    LandingHero,
    'eyebrow' | 'doctorName' | 'specialty' | 'city' | 'intro' | 'badgeTitle' | 'badgeText'
  >;
  readonly adBanner: Pick<LandingAdBanner, 'alt'>;
  readonly aboutDoctor: LandingDocument['aboutDoctor'];
  readonly aboutUs: LandingDocument['aboutUs'];
  readonly contact: Pick<LandingContact, 'adminContactText' | 'phoneHours'>;
  readonly footer: Pick<LandingDocument['footer'], 'tagline' | 'copyright' | 'legalNote'>;
  readonly seo: Pick<LandingDocument['seo'], 'title' | 'description'>;
  readonly bookingTerms: readonly LandingTerm[];
  readonly bookingGuide: readonly LandingGuideStep[];
  readonly faq: readonly LandingFaq[];
}

/**
 * «لوگو» — ids of uploaded images, or null. The site's logo leads the
 * landing's header and footer; the booking app's header has its own, since
 * that bar is narrow and follows the patient's light or dark theme.
 */
export interface LandingBranding {
  readonly siteLogoId: string | null;
  readonly bookingLogoId: string | null;
  /** A cut of the logo made for the dark footer; null shows the site logo on a white plate. */
  readonly footerLogoId: string | null;
  readonly logoSize: LandingLogoSize;
  /** «آیکون سایت» — the square mark in the browser tab and beside the site in Google. */
  readonly faviconId: string | null;
}

/** An image as a browser draws it: where, and at what intrinsic size. */
export interface LandingImageRef {
  readonly url: string;
  readonly width: number;
  readonly height: number;
}

/** An uploaded landing image — a public marketing asset, never PHI. */
export interface LandingImage {
  readonly id: string;
  readonly url: string;
  readonly mime: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
}

/**
 * What the panel edits: the **draft** — its version, content and images — and
 * where it stands against what is live. Nothing typed reaches patients until
 * the draft is published.
 */
export interface EditableLanding {
  /** The draft's version: what a save or a publish is checked against. */
  readonly version: number;
  readonly content: LandingDocument;
  readonly images: readonly LandingImage[];
  /** What is live, or null before the first publish. */
  readonly published: {
    /** The draft version that was published. */
    readonly version: number;
    readonly publishedAt: Date | null;
  } | null;
  /** Never published, or the draft differs from what is live. */
  readonly hasUnpublishedChanges: boolean;
}

/**
 * The part of the published view the booking app shows: the notice and the
 * clinic's contact details. The address and map link are Scheduling's
 * `ClinicLocation`, surfaced here rather than duplicated in the document.
 */
export interface PublishedLanding {
  readonly notice: LandingNotice;
  readonly contact: LandingContact;
  readonly location: {
    readonly addressLine: string | null;
    readonly mapUrl: string | null;
  };
  readonly bookingUrl: string;
  /** The booking app's own logo, or null for the plain mark. */
  readonly bookingLogo: LandingImageRef | null;
  /** The windows opened before paying for a بررسی آزمایش and a مشاوره. */
  readonly labReviewGate: LandingBookingGate;
  readonly consultationGate: LandingBookingGate;
  /**
   * False until the first publish, when the view is still the placeholder copy
   * — which must never reach patients.
   */
  readonly isPublished: boolean;
  /** Null until the clinic has published once. */
  readonly updatedAt: Date | null;
}

/**
 * The domain's limits, mirrored so the editor can say what is wrong before a
 * round trip. The server is authoritative: if the two ever disagree, its 422
 * is what the admin reads.
 */
export const LANDING_LIMITS = {
  // body is HTML from the rich-text editor, counted markup included
  notice: { barText: 140, title: 120, body: 8000, dateLabel: 40 },
  // body is HTML from the rich-text editor, counted markup included
  bookingGate: { title: 120, body: 8000, buttonLabel: 60 },
  labReviewGate: { title: 120, body: 8000, buttonLabel: 60 },
  consultationGate: { title: 120, body: 8000, buttonLabel: 60 },
  hero: {
    eyebrow: 80,
    doctorName: 80,
    specialty: 120,
    city: 60,
    intro: 400,
    badgeTitle: 60,
    badgeText: 80,
  },
  adBanner: { alt: 160 },
  aboutDoctor: {
    heading: 120,
    bio: 3000,
    credentialLabel: 60,
    credentialValue: 200,
    credentials: 12,
    service: 40,
    services: 20,
  },
  aboutUs: {
    heading: 120,
    intro: 2000,
    hoursDay: 30,
    hoursValue: 60,
    hours: 10,
    routeLabel: 30,
    routes: 5,
  },
  bookingTerms: { title: 80, body: 600, items: 12 },
  bookingGuide: { title: 80, body: 600, items: 12 },
  faq: { group: 60, question: 200, answer: 3000, items: 40 },
  contact: {
    adminContactText: 300,
    channelHandle: 40,
    channels: 3,
    phone: 20,
    phones: 3,
    phoneHours: 120,
  },
  footer: { tagline: 200, copyright: 160, legalNote: 200, enamadId: 20, enamadCode: 64 },
  seo: { title: 70, description: 170 },
  en: { addressLine: 200 },
} as const;

/** What the image endpoint accepts. The server re-checks the bytes themselves. */
export const LANDING_IMAGE_RULES = {
  maxMegabytes: 5,
  maxBytes: 5 * 1024 * 1024,
  /** Pixels, on either side. */
  maxDimension: 4000,
  mimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  /** The range the server takes for «بهینه‌سازی», in pixels on the longest side. */
  optimize: { min: 64, max: 4000 },
} as const;
